import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Download, Loader2, CalendarClock, FileBarChart } from 'lucide-react';
import { Button } from '../ui/button';
import { DateInputFR } from '../DateInputFR';
import { STATUTS, telecharger } from './shared';

const TYPES_AUDIT = [['', 'Type d\'audit'], ['initial', 'Audit initial'], ['surveillance', 'Audit de surveillance'], ['renouvellement', 'Audit de renouvellement']];

const joursAvant = (iso) => {
  if (!iso) return null;
  const j = Math.ceil((new Date(`${iso}T00:00:00`) - new Date().setHours(0, 0, 0, 0)) / 86400000);
  return j >= 0 ? `J-${j}` : 'Date passée';
};

const BarreScore = ({ score, total }) => (
  <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-white/10" data-testid="qualiopi-barre-score">
    {['vert', 'orange', 'rouge', 'na'].map((s) => score[s] > 0 && (
      <div key={s} className={`${STATUTS[s].pastille} transition-[width] duration-700`} style={{ width: `${(score[s] * 100) / total}%` }} />
    ))}
  </div>
);

const AuditConfig = ({ config, onSaved }) => {
  const [date, setDate] = useState(config.prochain_audit || '');
  const [fin, setFin] = useState(config.prochain_audit_fin || '');
  const [type, setType] = useState(config.type_audit || '');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDate(config.prochain_audit || ''); setFin(config.prochain_audit_fin || ''); setType(config.type_audit || ''); }, [config]);
  const modifie = date !== (config.prochain_audit || '') || fin !== (config.prochain_audit_fin || '') || type !== (config.type_audit || '');
  const enregistrer = async () => {
    setBusy(true);
    try {
      await axios.put('/qualiopi/config', { prochain_audit: date, prochain_audit_fin: fin, type_audit: type });
      toast.success('Prochain audit enregistré');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'enregistrement"); } finally { setBusy(false); }
  };
  const compte = joursAvant(config.prochain_audit);
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-4 space-y-3" data-testid="qualiopi-audit-config">
      <p className="flex items-center gap-2 text-sm font-medium text-white/80">
        <CalendarClock className="h-4 w-4 text-[#8DC63F]" /> Prochain audit
        {compte && <span className="ml-auto rounded-full bg-[#8DC63F] px-2.5 py-0.5 text-xs font-bold text-[#0F172A]" data-testid="qualiopi-audit-compte">{compte}</span>}
      </p>
      <div className="flex flex-wrap gap-2">
        <DateInputFR value={date} onChange={setDate} className="w-36 bg-white text-[#0F172A]" data-testid="qualiopi-audit-date" />
        <span className="self-center text-xs text-white/60">au</span>
        <DateInputFR value={fin} onChange={setFin} className="w-36 bg-white text-[#0F172A]" data-testid="qualiopi-audit-date-fin" />
        <select value={type} onChange={(e) => setType(e.target.value)} className="h-10 flex-1 min-w-[150px] rounded-md px-2 text-sm text-[#0F172A]" data-testid="qualiopi-audit-type">
          {TYPES_AUDIT.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {modifie && (
          <Button onClick={enregistrer} disabled={busy} className="bg-[#8DC63F] hover:bg-[#7AB534] text-[#0F172A]" data-testid="qualiopi-audit-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enregistrer'}
          </Button>
        )}
      </div>
    </div>
  );
};

const BilanAnnuel = () => {
  const an = new Date().getFullYear();
  const [annee, setAnnee] = useState(an);
  const [busy, setBusy] = useState(false);
  const generer = async () => {
    setBusy(true);
    try {
      await telecharger(`/qualiopi/bilan?annee=${annee}`, `Bilan_qualite_FISL_${annee}.pdf`, 60000);
      toast.success(`Bilan qualité ${annee} téléchargé`);
    } catch { toast.error('Impossible de générer le bilan'); } finally { setBusy(false); }
  };
  return (
    <div className="flex gap-2" data-testid="qualiopi-bilan">
      <select value={annee} onChange={(e) => setAnnee(Number(e.target.value))} className="h-11 rounded-md bg-white/10 px-2 text-sm text-white" data-testid="qualiopi-bilan-annee">
        {[an, an - 1, an - 2, an - 3].map((a) => <option key={a} value={a} className="text-[#0F172A]">{a}</option>)}
      </select>
      <Button onClick={generer} disabled={busy} variant="outline" className="h-11 flex-1 border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white" data-testid="qualiopi-bilan-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileBarChart className="h-4 w-4 mr-2" />} Bilan qualité annuel (PDF)
      </Button>
    </div>
  );
};

export const ScoreEntete = ({ data, onChanged }) => {
  const [zip, setZip] = useState(false);
  const { score } = data;
  const exporter = async () => {
    setZip(true);
    try {
      await telecharger('/qualiopi/dossier-zip', `Dossier_preuves_Qualiopi_FISL_${new Date().toISOString().slice(0, 10)}.zip`, 180000);
      toast.success('Dossier de preuves téléchargé');
    } catch { toast.error('Impossible de préparer le dossier ZIP'); } finally { setZip(false); }
  };
  return (
    <div className="rounded-2xl bg-[#0F172A] text-white p-5 sm:p-7 grid gap-6 lg:grid-cols-[1fr_380px]" data-testid="qualiopi-entete">
      <div className="space-y-4 min-w-0">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/60"><ShieldCheck className="h-4 w-4 text-[#8DC63F]" /> {data.guide}</div>
        <div className="flex flex-wrap items-end gap-x-6 gap-y-2">
          <p className="font-display text-6xl font-bold leading-none text-[#8DC63F]" data-testid="qualiopi-pourcentage">{score.pourcentage}<span className="text-3xl">%</span></p>
          <p className="text-lg text-white/90" data-testid="qualiopi-score">
            <strong>{score.vert}</strong> / {score.applicables} indicateurs validés
          </p>
        </div>
        <BarreScore score={score} total={data.indicateurs.length} />
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-white/80">
          {['vert', 'orange', 'rouge', 'na'].map((s) => (
            <span key={s} className="flex items-center gap-2" data-testid={`qualiopi-compte-${s}`}>
              <span className={`h-2.5 w-2.5 rounded-full ${STATUTS[s].pastille}`} /> {score[s]} {STATUTS[s].label.toLowerCase()}
            </span>
          ))}
        </div>
      </div>
      <div className="space-y-3">
        <AuditConfig config={data.config} onSaved={onChanged} />
        <Button onClick={exporter} disabled={zip} className="w-full h-11 bg-[#8DC63F] hover:bg-[#7AB534] text-[#0F172A] font-semibold" data-testid="qualiopi-export-zip">
          {zip ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Préparation du dossier…</> : <><Download className="h-4 w-4 mr-2" /> Dossier de preuves (ZIP)</>}
        </Button>
        <BilanAnnuel />
      </div>
    </div>
  );
};
